import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import User from "../../../model/User";
import {useResources} from "../../../stores/ResourceProvider";
import Account from "../../../model/Account";
import {ModelUserFilterInput} from "../../../API";
import {isoToLocalDate, numberToPercentFormat, getISODateFromDate} from "../../../stores/StoreUtilities";
import {globalColors} from "../../../styles/globalStyles";
import Chart from "react-apexcharts";
import LoadingProgress from "../../../components/LoadingProgress";
import MonthYearPicker from "../../../components/form/MonthYearPicker";
import cloneDeep from "lodash.clonedeep";
import {addMinutes, addMonths, addYears, startOfMonth} from "date-fns";


const defaultOptions = {
  chart: {
    id: "budget-projection",
    type: 'bar',
    stacked: false,
    height: 350,
    width: "100%",
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    }
  },
  title: {
    text: "2025",
    align: "right",
    floating: true,
    margin: 0,
    style: {
      fontSize: "16px",
      fontFamily: "Poppins,sans-serif",
      color: "rgb(31, 92, 167)"
    }
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    onItemClick: {
      toggleDataSeries: false
    }
  },
  xaxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    axisBorder: {
      show: true
    }
  },
  yaxis: {
    // labels: {
    //   formatter: (value: number) => {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  tooltip: {
    // y: {
    //   formatter: function (value: number) {
    //     return numberToMoneyFormat(value, 0)
    //   }
    // }
  },
  colors: globalColors.categorical,
  responsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const ConversionsReport = ({width}: {width: number}) => {
  const [account, setAccount] = useState<Account | undefined>()
  const [users, setUsers] = useState<User[]>([])
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [totals, setTotals] = useState<number[]>([])
  const [startDate, setStartDate] = useState<Date>(addMonths(startOfMonth(new Date()), -11))
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const {userStore, accountStore, notify} = useResources()

  useEffect(() => {
    if (userStore.isLoading === false && userStore.user && userStore.user.account) {
      setAccount(userStore.user.account)
      setUsers([])
      if (!isLoading) {
        loadUsers(userStore.user.account)
      }
    }
  }, [userStore.isLoading, startDate])

  useEffect(() => {
    // if (users.length > 0) {
      calculateData()
    // }
  }, [users])

  const loadUsers = async (account: Account) => {
    try {
      setIsLoading(true)
      let endDate = addMinutes(addYears(startDate, 1), -1)
      let filter : ModelUserFilterInput | undefined =
        {
          startDate: {between: [getISODateFromDate(startDate), getISODateFromDate(endDate)]}
        }
      let list = await accountStore.listUsersByAccount(account.id, filter)
      setUsers(list)
    } catch (err: any) {
      console.error("loadUsers error", err)
      notify.show("error", err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const calculateData = () => {
    let signups = new Array<number>(12).fill(0)
    let subscriptions = new Array<number>(12).fill(0)
    let cancellations = new Array<number>(12).fill(0)
    let deactivations = new Array<number>(12).fill(0)
    let clients = new Array<number>(12).fill(0)
    let totals = new Array<number>(5).fill(0)

    const startMonth = startDate.getMonth()

    const filteredUsers = users.filter((user: User) => user.startDate && user.startDate >= "2025-01-01")
    filteredUsers.forEach((user: User) => {
      let signupMonth = isoToLocalDate(user.startDate).getMonth()
      if (signupMonth < startMonth) {
        signupMonth += 12
      }
      signupMonth -= startMonth
      signups[signupMonth]++
      totals[0]++
      if (user.subscribedOn) {
        let subscribedMonth = isoToLocalDate(user.subscribedOn).getMonth()
        if (subscribedMonth < startMonth) {
          subscribedMonth += 12
        }
        subscribedMonth -= startMonth
        subscriptions[subscribedMonth]++
        totals[1]++
      }
      if (user.clientOn) {
        let clientMonth = isoToLocalDate(user.clientOn).getMonth()
        if (clientMonth < startMonth) {
          clientMonth += 12
        }
        clientMonth -= startMonth
        clients[clientMonth]++
        totals[2]++
      }
      if (user.cancelledOn) {
        let cancelledMonth = isoToLocalDate(user.cancelledOn).getMonth()
        if (cancelledMonth < startMonth) {
          cancelledMonth += 12
        }
        cancelledMonth -= startMonth
        if (user.customerId) {
          cancellations[cancelledMonth]++
          totals[3]++
        } else {
          deactivations[cancelledMonth]++
          totals[4]++
        }
      }
      if (user.deletedOn) {
        let deletedMonth = isoToLocalDate(user.deletedOn).getMonth()
        if (deletedMonth < startMonth) {
          deletedMonth += 12
        }
        deletedMonth -= startMonth
        if (user.customerId) {
          cancellations[deletedMonth]++
          totals[3]++
        } else {
          deactivations[deletedMonth]++
          totals[4]++
        }
      }
    })

    const series = [
      {
        name: "Signups",
        data: signups
      },
      {
        name: "Subscriptions",
        data: subscriptions
      },
      {
        name: "New Clients",
        data: clients
      },
      {
        name: "Cancellations",
        data: cancellations
      },
      {
        name: "Deactivations",
        data: deactivations
      }
    ]

    const options = cloneDeep(defaultOptions)

    // Adjust xAxis categories, if not starting in Jan
    if (startMonth > 0) {
      const rotated = [...monthLabels.slice(startMonth), ...monthLabels.slice(0, startMonth)]
      options.xaxis.categories = rotated
    }
    setOptions(options)

    setSeries(series)
    setTotals(totals)
  }

  const renderTable = (series: any[]) => {
    let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    const startMonth = startDate.getMonth()
    if (startMonth > 0) {
      months = [...months.slice(startMonth), ...months.slice(0, startMonth)]
    }

    return (
      <Box display="flex" flexGrow={1} flexDirection="column" mt={2}>
        <Box display="flex" flexGrow={1} justifyContent="flex-start" alignItems="center" maxHeight={40} mb={1}>
          <Typography variant="h3" color="primary" sx={{mr:2}}>Conversions</Typography>
          <MonthYearPicker value={startDate}
                           onChange={(value: Date) => setStartDate(value)} />
        </Box>
        <TableContainer component={Paper} sx={{paddingTop:0}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="15%">Month</TableCell>
                {series.map((item: any) =>
                  <TableCell width="15%" align="right">{item.name}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {months.map((month: string, index: number) =>
                <TableRow key={`row${index}`}>
                  <TableCell>{month}</TableCell>
                  <TableCell align="right">{series[0].data[index]}</TableCell>
                  <TableCell align="right">{series[1].data[index]} {`(${numberToPercentFormat(series[1].data[index] / series[0].data[index], 1)})`}</TableCell>
                  <TableCell align="right">{series[2].data[index]}</TableCell>
                  <TableCell align="right">{series[3].data[index]}</TableCell>
                  <TableCell align="right">{series[4].data[index]}</TableCell>
                </TableRow>
              )}
              <TableRow key={`totals`}>
                <TableCell><b>Totals</b></TableCell>
                <TableCell align="right"><b>{totals[0]}</b></TableCell>
                <TableCell align="right"><b>{totals[1]} {`(${numberToPercentFormat(totals[1] / totals[0], 1)})`}</b></TableCell>
                <TableCell align="right"><b>{totals[2]}</b></TableCell>
                <TableCell align="right"><b>{totals[3]}</b></TableCell>
                <TableCell align="right"><b>{totals[4]}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:"350px", width:width-32}}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <LoadingProgress marginTop="150px"/>
        </Box>
      </TableContainer>
    )
  }

  if (options && series) {
    return (
      <Box display="flex" flexGrow={1} flexDirection="column" width={width-32} overflow="scroll">
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden", minHeight:"350px"}}>
          <Chart
            options={options}
            series={series}
            type="bar"
            height="350"
            width={`${width-32}px`}
          />
        </TableContainer>

        {renderTable(series)}
      </Box>
    )
  } else {
    return null
  }
}

export default ConversionsReport